/**
 * CraveTheory Lenis — stable smooth scroll
 *
 * Desktop (≥990px): smooths `.page-wrapper` (Horizon squeeze container)
 * Mobile / coarse pointer: native document scroll (no Lenis) so address-bar
 * and touch inertia stay correct.
 *
 * Lag / stickiness causes this revision removes:
 * 1. MutationObserver on the Lenis root watching `class` — Lenis toggles
 *    `lenis-scrolling` / `lenis-smooth` every frame, which re-entered
 *    syncLockState and janked the main thread mid-scroll.
 * 2. Dynamic lerp adaptation that snapped back to base after 90ms and made
 *    coasting feel sticky.
 * 3. Redundant ResizeObservers stacking on top of Lenis `autoResize`, fired
 *    by frequent `ct:scale` events and fighting the scroll limit.
 * 4. `allowNestedScroll` fighting horizontal overflow regions (pairs grid).
 */
import Lenis from '@theme/lenis';
import {
  getScrollContainer,
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const TOUCH_COARSE = window.matchMedia('(hover: none) and (pointer: coarse)');

/** Single stable lerp — no mid-gesture switching */
const LERP = 0.12;
/** Slight under-drive reduces end-of-range rubber / sticky feel */
const WHEEL_MULTIPLIER = 0.9;

const LOCK_CLASS_TOKENS = [
  'ct-drawer-open',
  'ct-cart-drawer-open',
  'scroll-lock',
  'page-wrapper--drawer-open',
];

/** @type {import('lenis').default | null} */
let lenis = null;
/** @type {MutationObserver | null} */
let lockObserver = null;
/** @type {number} */
let resizeTimer = 0;
/** @type {boolean} */
let starting = false;
/** @type {boolean} */
let pendingResize = false;

/**
 * @returns {import('lenis').default | null}
 */
export function getLenis() {
  return lenis;
}

/**
 * Prefer native scrolling on touch phones/tablets.
 * Trackpads / mice keep Lenis.
 * @returns {boolean}
 */
function shouldUseNativeScroll() {
  return REDUCED.matches || TOUCH_COARSE.matches;
}

/**
 * @returns {boolean}
 */
function shouldStop() {
  const html = document.documentElement;
  if (html.hasAttribute('scroll-lock')) return true;
  if (html.classList.contains('ct-drawer-open')) return true;
  if (html.classList.contains('ct-cart-drawer-open')) return true;
  if (document.body?.classList.contains('scroll-lock')) return true;
  if (document.querySelector('.page-wrapper--drawer-open')) return true;
  return false;
}

function syncLockState() {
  if (!lenis) return;
  const stop = shouldStop();
  if (stop) {
    if (!lenis.isStopped) lenis.stop();
  } else if (lenis.isStopped) {
    lenis.start();
  }
}

/**
 * Only treat dedicated scroll traps as prevent — keep page wheel clean.
 * @param {HTMLElement | Element | null | undefined} node
 * @returns {boolean}
 */
function shouldPrevent(node) {
  if (!(node instanceof HTMLElement)) return false;
  return Boolean(
    node.closest(
      [
        '[data-lenis-prevent]',
        '[data-lenis-prevent-wheel]',
        '[data-lenis-prevent-touch]',
        '[data-lenis-prevent-vertical]',
        '[data-ct-drawer-panel]',
        '.ct-drawer__panel',
        '.ct-cart-drawer__panel',
        '.facets__panel',
        '.predictive-search',
        '.dialog-modal',
      ].join(', ')
    )
  );
}

/**
 * Debounced resize. Skips while coasting so limit recalcs don’t hitch the scroll.
 * @param {number} [delay]
 */
function scheduleResize(delay = 180) {
  if (!lenis) return;

  if (lenis.isScrolling) {
    pendingResize = true;
    return;
  }

  window.clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(() => {
    resizeTimer = 0;
    if (!lenis) return;
    if (lenis.isScrolling) {
      pendingResize = true;
      return;
    }
    pendingResize = false;
    lenis.resize();
  }, delay);
}

function flushPendingResize() {
  if (!pendingResize || !lenis) return;
  if (lenis.isScrolling) return;
  pendingResize = false;
  lenis.resize();
}

/**
 * True when a class mutation actually flipped a lock-related token.
 * Ignores Lenis’s own `lenis-*` class churn (the previous jank source).
 * @param {string} prev
 * @param {string} next
 */
function lockClassChanged(prev, next) {
  return LOCK_CLASS_TOKENS.some((token) => {
    const a = prev.split(/\s+/).includes(token);
    const b = next.split(/\s+/).includes(token);
    return a !== b;
  });
}

function destroyLenis() {
  if (lockObserver) {
    lockObserver.disconnect();
    lockObserver = null;
  }
  if (resizeTimer) {
    window.clearTimeout(resizeTimer);
    resizeTimer = 0;
  }
  pendingResize = false;
  if (lenis) {
    lenis.destroy();
    lenis = null;
  }
  window.__ctLenis = null;
  document.documentElement.classList.remove(
    'ct-lenis',
    'ct-lenis-native',
    'ct-lenis-wrapper',
    'lenis',
    'lenis-smooth',
    'lenis-scrolling',
    'lenis-stopped',
    'lenis-locked'
  );
  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper) {
    wrapper.classList.remove(
      'lenis',
      'lenis-smooth',
      'lenis-scrolling',
      'lenis-stopped',
      'lenis-locked'
    );
  }
}

function createLenis() {
  if (shouldUseNativeScroll() || starting) {
    if (shouldUseNativeScroll()) destroyLenis();
    return;
  }

  starting = true;

  const preservedY = (() => {
    try {
      const existing = window.__ctLenis;
      if (existing && typeof existing.scroll === 'number') return existing.scroll;
      return getScrollContainer().scrollTop || 0;
    } catch {
      return 0;
    }
  })();

  destroyLenis();

  const squeeze = scrollContainerMediaQuery.matches;
  const pageWrapper = document.querySelector('.page-wrapper');

  /** @type {ConstructorParameters<typeof Lenis>[0]} */
  const options = {
    autoRaf: true,
    // Lenis owns resize observation — do not stack another ResizeObserver
    autoResize: true,
    anchors: true,
    // Nested-scroll arbitration was fighting horizontal carousels / pairs grids
    allowNestedScroll: false,
    stopInertiaOnNavigate: true,
    syncTouch: false,
    smoothWheel: true,
    lerp: LERP,
    wheelMultiplier: WHEEL_MULTIPLIER,
    touchMultiplier: 1.2,
    infinite: false,
    orientation: 'vertical',
    gestureOrientation: 'vertical',
    overscroll: true,
    prevent: shouldPrevent,
  };

  if (squeeze && pageWrapper instanceof HTMLElement) {
    options.wrapper = pageWrapper;
    options.content = pageWrapper;
    options.eventsTarget = pageWrapper;
    options.naiveDimensions = true;
    document.documentElement.classList.add('ct-lenis', 'ct-lenis-wrapper');
  } else {
    document.documentElement.classList.add('ct-lenis', 'ct-lenis-native');
  }

  lenis = new Lenis(options);
  window.__ctLenis = lenis;

  if (preservedY > 0) {
    lenis.scrollTo(preservedY, { immediate: true, force: true });
  }

  // Flush deferred resizes only once coasting has settled
  lenis.on('scroll', () => {
    if (!lenis?.isScrolling) flushPendingResize();
  });

  // Lock observer — NEVER watch Lenis root `class` blindly.
  // Only react when a lock-related token actually flips.
  lockObserver = new MutationObserver((records) => {
    for (const record of records) {
      if (record.attributeName === 'scroll-lock') {
        syncLockState();
        return;
      }
      if (record.attributeName === 'class') {
        const prev = record.oldValue || '';
        const next =
          record.target instanceof Element ? record.target.getAttribute('class') || '' : '';
        if (lockClassChanged(prev, next)) {
          syncLockState();
          return;
        }
      }
    }
  });

  lockObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['scroll-lock', 'class'],
    attributeOldValue: true,
  });

  if (document.body) {
    lockObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
    });
  }

  // page-wrapper--drawer-open only — ignore Lenis’s per-frame class churn
  if (pageWrapper) {
    lockObserver.observe(pageWrapper, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
    });
  }

  document.addEventListener('ct-cart:open', onCartOpen);
  document.addEventListener('ct-cart:close', onCartClose);

  syncLockState();
  starting = false;
}

function onCartOpen() {
  if (lenis && !lenis.isStopped) lenis.stop();
}

function onCartClose() {
  syncLockState();
}

function boot() {
  // Drop prior cart listeners before rebuild
  document.removeEventListener('ct-cart:open', onCartOpen);
  document.removeEventListener('ct-cart:close', onCartClose);

  if (shouldUseNativeScroll()) {
    destroyLenis();
    return;
  }
  createLenis();
}

boot();

scrollContainerMediaQuery.addEventListener('change', () => {
  requestAnimationFrame(boot);
});

REDUCED.addEventListener('change', () => {
  requestAnimationFrame(boot);
});

TOUCH_COARSE.addEventListener('change', () => {
  requestAnimationFrame(boot);
});

// Scale/layout changes — debounced, never mid-coast
window.addEventListener('ct:scale', () => scheduleResize(280), { passive: true });
window.addEventListener('load', () => scheduleResize(50));

document.addEventListener('shopify:section:load', () => scheduleResize(120));
document.addEventListener('shopify:section:unload', () => scheduleResize(120));

window.addEventListener('pageshow', () => {
  requestAnimationFrame(() => {
    scheduleResize(50);
    syncLockState();
  });
});

export { getScrollContainer, getScrollEventTarget };
